import type { WorkingTimeData, WorkingTimeDays } from '@nsftx/systems-sdk'
import { defineStore } from 'pinia'
import { readonly, ref } from 'vue'
import { format, parse, isAfter, isBefore, isEqual } from 'date-fns'
import { logService } from '@nsftx/seven-client-retail-utils'
import {
  getScheduleTimeForCurrentValidTime,
  getNextDayMidnightScheduleTime,
  getScheduleTimeForCurrentInvalidTime,
  getNextValidTime
} from '#src/services/workingTimeUtils'
import type { WorkingTimeValidationDetails } from '#src/types/WorkingTimeTypes'

const LOG_PREFIX = '[workingTimeStore]'

export const useWorkingTimeStore = defineStore('workingTimeStore', () => {
  const workingTimeData = ref<WorkingTimeData>()

  const setWorkingTimeData = (data: WorkingTimeData) => {
    logService.debug(`${LOG_PREFIX} Setting working time data`, {
      code: 'DM_SET_WORKING_TIME_DATA',
      details: {
        data
      }
    })
    workingTimeData.value = data
  }

  const isWorkingTimeValid = (): WorkingTimeValidationDetails => {
    const TIME_FORMAT = 'HH:mm:ss'
    const currentDay = format(new Date(), 'EEEE').toLowerCase()
    const validTimesPerDay =
      workingTimeData.value?.[`working_time_${currentDay}` as WorkingTimeDays]
    const referenceDate = new Date()
    const currentTime = format(new Date(), TIME_FORMAT)
    const currentParsedTime = parse(currentTime, TIME_FORMAT, referenceDate)
    let isValid = false
    let detectedScheduleTime

    if (!validTimesPerDay || validTimesPerDay.length === 0) {
      return {
        isValid: false,
        nextValidTime: getNextValidTime(workingTimeData.value as WorkingTimeData),
        scheduleTime: getNextDayMidnightScheduleTime()
      }
    }

    validTimesPerDay.forEach((validTime) => {
      const startTime = parse(validTime.start, TIME_FORMAT, referenceDate)
      const endTime = parse(validTime.end, TIME_FORMAT, referenceDate)

      // Check if current time is within the range
      const isTimeValid =
        isEqual(currentParsedTime, startTime) ||
        isEqual(currentParsedTime, endTime) ||
        (isAfter(currentParsedTime, startTime) && isBefore(currentParsedTime, endTime))

      if (isTimeValid) {
        isValid = true
        detectedScheduleTime = getScheduleTimeForCurrentValidTime(validTime)
      }
    })

    if (isValid) {
      return {
        isValid: true,
        scheduleTime: detectedScheduleTime || getNextDayMidnightScheduleTime()
      }
    }

    const scheduleTime = getScheduleTimeForCurrentInvalidTime(validTimesPerDay)

    return {
      isValid: false,
      nextValidTime: getNextValidTime(workingTimeData.value as WorkingTimeData),
      scheduleTime
    }
  }

  return {
    workingTimeData: readonly(workingTimeData),
    setWorkingTimeData,
    isWorkingTimeValid
  }
})
